/**
 * Adresses trompeuses : un nom de site ecrit avec des lettres d'un AUTRE alphabet qui imitent le latin
 * (attaque « homographe » IDN). `аpple.com`, dont le premier « а » est cyrillique, s'affiche comme `apple.com`
 * et n'est pas apple.com : un mot de passe enregistre a cette adresse a ete saisi sur un site d'hameconnage, et
 * le coffre le remplirait la-bas.
 *
 * Les deux regles sont celles qu'applique Chromium avant d'afficher un nom internationalise en clair
 * (components/url_formatter/spoof_checks/idn_spoof_checker.cc), elles-memes tirees d'Unicode UTS #39 :
 * - `mixed-script` : un meme label melange plusieurs ecritures, au sens du niveau « highly restrictive » d'UTS #39
 *   (§ 5.2) que Chromium impose : une seule ecriture, ou le latin avec UNE ecriture CJK logique (chinois : han +
 *   bopomofo ; japonais : han + kana ; coreen : han + hangul). `аpple`, `abᒿ` (syllabaire canadien), `liⵏy` (tifinagh),
 *   `한ひら` (hangul + hiragana) sont vises ; `sonyストア`, `漢字hanzi` ne le sont pas. Une lettre latine hors ASCII a
 *   cote du CJK l'est aussi (`café카페` ; Chromium : `kNonAsciiLatinCharMixedWithNonLatin`) ;
 * - `whole-script` : un label entierement cyrillique, grec, armenien, georgien, ethiopien, hebreu, thai, indien
 *   (bengali, devanagari, gujarati, gurmukhi, kannada, malayalam, oriya, tamoul, telougou) ou birman, fait des seuls
 *   caracteres qui ont un sosie latin, sous un domaine de premier niveau qui n'est pas de cette ecriture
 *   (`аррӏе.com`, `κιηο.com`, `յօհո.com`, `ሠዐዐፐ.com`, `ഠട.com`, mais pas `сайт.рф`, `ресурс.ru`, `κιηο.gr` ni `ሠዐዐፐ.et`) ;
 * - `digit-lookalike` : un label fait de chiffres et d'au moins un caractere qui imite un chiffre (`1շ.com`, `੨੨੨.com`,
 *   `123456789θ.com`), quel que soit le domaine de premier niveau (Chromium : `kDigitLookalikes`).
 * - `zone-character` : un caractere que Chromium ne laisse qu'a une zone (`þ` et `ð` islandais hors `.is` et `.fo`, `ə`
 *   azeri hors `.az` : `aþcdef.com`, `əxample.com` ; Chromium : `kTLDSpecificCharacters`) ;
 * - `middle-dot` : le point median `·`, admis seulement sous `.cat` entre deux `l` (`l·l.cat`, la ela geminada catalane ;
 *   `google·com.com` est vise ; Chromium : `kUnsafeMiddleDot`) ;
 * - `dangerous-pattern` : les motifs dangereux de Chromium (`kDangerousPattern`) -- un signe japonais ou chinois qui
 *   imite une barre, un tiret ou un point a cote de ce qui n'est pas japonais ou chinois (`google.comノevil.jp`,
 *   `ip一address.com`, `abc・def.jp`, `日本ー野球.jp`), un accent combinant hors latin, grec et cyrillique (`한̇글.com`) ou
 *   un point suscrit sur `i`, `j`, `l` (`pi̇xel.com`) ;
 * - `disallowed-character` : un caractere hors du jeu que Chromium admet dans un nom internationalise
 *   (`SetAllowedUnicodeSet` : UTS #39 `Recommended` + `Inclusion`, moins ses retraits) -- ecriture d'usage limite ou exclue
 *   (`ꔋ.com` vai, `ꮓꭺꭱꭰ.com` cherokee, `ϧ.com` copte), petite capitale ou lettre IPA (`ɢᴏᴏɢʟᴇ.com`), symbole, tiret,
 *   apostrophe (`abc–def.com`, `aʻb.com`), latin ou cyrillique d'usage rarissime (`ǔnion.com`, `ḷily.com`, `oĸ.com`).
 * - `top-domain` : un nom par ailleurs admis dont le squelette (UTS #39) est celui d'un des domaines les plus visites
 *   selon Chromium (`googlé.com` -> google.com, `ąmazon.de` -> amazon.de, `łwitter.com` -> twitter.com ; Chromium : `GetSimilarTopDomain`, liste
 *   `top_domains/domains.skeletons`). Un sous-domaine du domaine lui-meme n'est pas vise (`subdómain.google.com`).
 * - `target-embedding` : un nom, internationalise ou non, qui ENCHASSE celui d'un domaine tres visite ou d'un site du
 *   coffre avant son vrai domaine (`google.com-login.net`, `paypal.com.secure-verify.net`, `googlecom-login.net`,
 *   `www-google.com` ; Chromium : `GetTargetEmbeddingType`, heuristique « lookalike » de la navigation). La barre
 *   d'adresse coupee a droite ne montre que le nom enchasse.
 *
 * Pur, sans E/S ni reseau. Le punycode est decode ici (RFC 3492, § 6.2) : le navigateur sait l'encoder (`URL`),
 * pas le decoder.
 */
import { knownRegistrableDomain, siteHostname } from '@klavox/vault-model';
import { IDN_ALLOWED_RANGES } from './idn-allowed-data.js';
import { IDN_COMMON_WORDS } from './idn-common-words-data.js';
import { IDN_CONFUSABLES_ASCII, IDN_TOP_BUCKET_COUNT, IDN_TOP_DOMAINS } from './idn-top-domains-data.js';

const BASE = 36;
const TMIN = 1;
const TMAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;
const MAX_INT = 0x7fffffff;

function adapt(delta: number, numPoints: number, firstTime: boolean): number {
  let d = firstTime ? Math.floor(delta / DAMP) : Math.floor(delta / 2);
  d += Math.floor(d / numPoints);
  let k = 0;
  while (d > ((BASE - TMIN) * TMAX) >> 1) {
    d = Math.floor(d / (BASE - TMIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - TMIN + 1) * d) / (d + SKEW));
}

function digitValue(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 22;
  if (code >= 0x41 && code <= 0x5a) return code - 0x41;
  if (code >= 0x61 && code <= 0x7a) return code - 0x61;
  return BASE;
}

/**
 * Decode un label punycode SANS son prefixe `xn--` (RFC 3492, § 6.2). `null` si l'entree est invalide : debordement,
 * chiffre hors base, point de code hors Unicode ou demi-substitut.
 */
export function decodePunycode(input: string): string | null {
  const output: number[] = [];
  const delimiter = input.lastIndexOf('-');
  for (let j = 0; j < Math.max(delimiter, 0); j += 1) {
    const code = input.charCodeAt(j);
    if (code >= 0x80) return null;
    output.push(code);
  }
  let n = INITIAL_N;
  let bias = INITIAL_BIAS;
  let i = 0;
  for (let index = delimiter > 0 ? delimiter + 1 : 0; index < input.length; ) {
    const oldI = i;
    let w = 1;
    for (let k = BASE; ; k += BASE) {
      if (index >= input.length) return null;
      const digit = digitValue(input.charCodeAt(index));
      index += 1;
      if (digit >= BASE || digit > Math.floor((MAX_INT - i) / w)) return null;
      i += digit * w;
      const t = k <= bias ? TMIN : k >= bias + TMAX ? TMAX : k - bias;
      if (digit < t) break;
      if (w > Math.floor(MAX_INT / (BASE - t))) return null;
      w *= BASE - t;
    }
    const length = output.length + 1;
    bias = adapt(i - oldI, length, oldI === 0);
    if (Math.floor(i / length) > MAX_INT - n) return null;
    n += Math.floor(i / length);
    i %= length;
    if (n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff)) return null;
    output.splice(i, 0, n);
    i += 1;
  }
  return String.fromCodePoint(...output);
}

/** Ecritures dont des lettres se confondent avec le latin (UTS #39, table des confusables) : celles du sosie entier. */
const CONFUSABLE_SCRIPTS: readonly (readonly [string, RegExp])[] = [
  ['latin', /\p{Script=Latin}/u],
  ['cyrillic', /\p{Script=Cyrillic}/u],
  ['greek', /\p{Script=Greek}/u],
  ['armenian', /\p{Script=Armenian}/u],
  ['cherokee', /\p{Script=Cherokee}/u],
  ['georgian', /\p{Script=Georgian}/u],
  ['ethiopic', /\p{Script=Ethiopic}/u],
  ['hebrew', /\p{Script=Hebrew}/u],
  ['thai', /\p{Script=Thai}/u],
  ['bengali', /\p{Script=Bengali}/u],
  ['devanagari', /\p{Script=Devanagari}/u],
  ['gujarati', /\p{Script=Gujarati}/u],
  ['gurmukhi', /\p{Script=Gurmukhi}/u],
  ['kannada', /\p{Script=Kannada}/u],
  ['malayalam', /\p{Script=Malayalam}/u],
  ['oriya', /\p{Script=Oriya}/u],
  ['tamil', /\p{Script=Tamil}/u],
  ['telugu', /\p{Script=Telugu}/u],
  ['myanmar', /\p{Script=Myanmar}/u],
];

const SCRIPT_PATTERNS: ReadonlyMap<string, RegExp> = new Map(CONFUSABLE_SCRIPTS);

/**
 * Toutes les ecritures d'Unicode (codes ISO 15924 de `PropertyValueAliases.txt`, Unicode 17.0), sauf Common, Inherited,
 * Unknown et Katakana_Or_Hiragana, qu'aucun caractere ne porte. Un moteur qui ne connait pas une ecriture plus recente la
 * saute : ses caracteres comptent alors comme communs, ils n'arretent pas l'application.
 */
const SCRIPT_EXTENSIONS: readonly (readonly [string, RegExp])[] = (
  'Adlm Aghb Ahom Arab Armi Armn Avst Bali Bamu Bass Batk Beng Berf Bhks Bopo Brah Brai Bugi Buhd Cakm Cans Cari Cham Cher '
  + 'Chrs Copt Cpmn Cprt Cyrl Deva Diak Dogr Dsrt Dupl Egyp Elba Elym Ethi Gara Geor Glag Gong Gonm Goth Gran Grek Gujr Gukh '
  + 'Guru Hang Hani Hano Hatr Hebr Hira Hluw Hmng Hmnp Hung Ital Java Kali Kana Kawi Khar Khmr Khoj Kits Knda Krai Kthi Lana '
  + 'Laoo Latn Lepc Limb Lina Linb Lisu Lyci Lydi Mahj Maka Mand Mani Marc Medf Mend Merc Mero Mlym Modi Mong Mroo Mtei Mult '
  + 'Mymr Nagm Nand Narb Nbat Newa Nkoo Nshu Ogam Olck Onao Orkh Orya Osge Osma Ougr Palm Pauc Perm Phag Phli Phlp Phnx Plrd '
  + 'Prti Rjng Rohg Runr Samr Sarb Saur Sgnw Shaw Shrd Sidd Sidt Sind Sinh Sogd Sogo Sora Soyo Sund Sunu Sylo Syrc Tagb Takr '
  + 'Tale Talu Taml Tang Tavt Tayo Telu Tfng Tglg Thaa Thai Tibt Tirh Tnsa Todr Tols Toto Tutg Ugar Vaii Vith Wara Wcho Xpeo '
  + 'Xsux Yezi Yiii Zanb'
)
  .split(' ')
  .flatMap((code) => {
    try {
      return [[code, new RegExp(`\\p{Script_Extensions=${code}}`, 'u')] as const];
    } catch {
      return [];
    }
  });

const augmentedScriptsCache = new Map<string, ReadonlySet<string> | null>();

/**
 * Les ecritures d'un caractere (`Script_Extensions`, pas `Script` : `ー` est commun mais japonais), augmentees comme
 * UTS #39 § 5.1 : le han vaut chinois (`Hanb`), japonais (`Jpan`) et coreen (`Kore`), les kana japonais, le hangul coreen,
 * le bopomofo chinois. `null` pour un caractere commun ou herite (chiffre, tiret, accent combinant) : il va avec tout.
 */
function augmentedScripts(char: string): ReadonlySet<string> | null {
  const cached = augmentedScriptsCache.get(char);
  if (cached !== undefined) return cached;
  const scripts = new Set<string>();
  for (const [code, pattern] of SCRIPT_EXTENSIONS) if (pattern.test(char)) scripts.add(code);
  if (scripts.has('Hani')) for (const code of ['Hanb', 'Jpan', 'Kore']) scripts.add(code);
  if (scripts.has('Hira') || scripts.has('Kana')) scripts.add('Jpan');
  if (scripts.has('Hang')) scripts.add('Kore');
  if (scripts.has('Bopo')) scripts.add('Hanb');
  const result = scripts.size === 0 ? null : scripts;
  augmentedScriptsCache.set(char, result);
  return result;
}

/**
 * Ensemble d'ecritures resolu d'un label (UTS #39 § 5.1, `getResolvedScriptSetWithout` d'ICU) : les ecritures que tous ses
 * caracteres partagent, en sautant ceux qui portent `without`. `null` = toutes (aucun caractere ne restreint).
 */
function resolvedScripts(label: string, without?: string): ReadonlySet<string> | null {
  let resolved = null as Set<string> | null;
  for (const char of label) {
    const scripts = augmentedScripts(char);
    if (scripts === null || (without !== undefined && scripts.has(without))) continue;
    resolved = resolved === null ? new Set(scripts) : new Set([...resolved].filter((code) => scripts.has(code)));
  }
  return resolved;
}

/**
 * Le niveau de restriction d'un label (UTS #39 § 5.2, `getRestrictionLevel` d'ICU), au seuil « highly restrictive » que
 * Chromium impose : `single` = une seule ecriture (chinois, japonais et coreen comptent chacun pour une) ; `latin-cjk` =
 * le latin avec UNE ecriture CJK logique (`sonyストア`, `漢字hanzi`) ; `mixed` = tout autre melange (`abᒿ`, `foo༷`, `한ひら`).
 */
function scriptMix(label: string): 'single' | 'latin-cjk' | 'mixed' {
  const resolved = resolvedScripts(label);
  if (resolved === null || resolved.size > 0) return 'single';
  const withoutLatin = resolvedScripts(label, 'Latn');
  if (withoutLatin === null || withoutLatin.has('Hanb') || withoutLatin.has('Jpan') || withoutLatin.has('Kore')) return 'latin-cjk';
  return 'mixed';
}

/** Lettre latine hors ASCII (`é`) : Chromium la refuse a cote d'une ecriture CJK (`kNonAsciiLatinCharMixedWithNonLatin`). */
const NON_ASCII_LATIN_LETTER = /(?![a-zA-Z])\p{Script=Latin}/u;

/**
 * Lettres cyrilliques qui ont un sosie latin : l'ensemble cyrillique de `kWholeScriptConfusables` de Chromium tel quel.
 * Le sosie des lettres de `cyrillic_letters_latin_alike_` est celui de Chromium ; pour `ы`, `ԍ`, `ю`, `к`, `м`, `ԗ`, `т`,
 * la table UTS #39 ne donne pas de lettre latine ordinaire (`ĸ`, `ʍ`, petites capitales `ɢ` `ᴛ`, ou rien) : `null`.
 */
const CYRILLIC_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['а', 'a'], ['с', 'c'], ['ԁ', 'd'], ['е', 'e'], ['һ', 'h'], ['і', 'i'], ['ј', 'j'], ['ӏ', 'l'],
  ['о', 'o'], ['р', 'p'], ['ԛ', 'q'], ['ѕ', 's'], ['ԝ', 'w'], ['х', 'x'], ['у', 'y'], ['ъ', 'b'],
  ['ь', 'b'], ['ҽ', 'e'], ['п', 'n'], ['г', 'r'], ['ѵ', 'v'], ['ѡ', 'w'],
  ['ы', null], ['ԍ', null], ['ю', null], ['к', null], ['м', null], ['ԗ', null], ['т', null],
]);

/** Mots courants faits de sosies, qui n'imitent aucun mot latin : la liste `kAllowedWholeScriptConfusableWords` de Chromium. */
const ALLOWED_WHOLE_SCRIPT_WORDS: ReadonlySet<string> = new Set([
  'секс', 'как', 'коса', 'курс', 'парк', 'такий', 'укроп', 'сахарок', 'покраска', 'театр', 'астро', 'пхукет',
]);

/** Domaines de premier niveau des pays d'ecriture cyrillique (liste de Chromium) : un nom cyrillique y est attendu. */
const CYRILLIC_TLDS: ReadonlySet<string> = new Set(['bg', 'by', 'kz', 'pyc', 'ru', 'su', 'ua', 'uz']);

/** Lettres grecques qui ont un sosie latin : l'ensemble grec des `WholeScriptConfusableData` de Chromium. */
const GREEK_LATIN_ALIKE: ReadonlyMap<string, string> = new Map([
  ['α', 'a'], ['ι', 'i'], ['κ', 'k'], ['ν', 'v'], ['ρ', 'p'], ['υ', 'u'], ['ω', 'w'], ['η', 'n'], ['ο', 'o'], ['τ', 't'],
]);

/**
 * Lettres armeniennes qui ont un sosie latin : l'ensemble armenien de Chromium, hors `է`, `ւ` et `ՙ` dont le sosie
 * n'est pas une lettre latine nette.
 */
const ARMENIAN_LATIN_ALIKE: ReadonlyMap<string, string> = new Map([
  ['ա', 'w'], ['գ', 'q'], ['զ', 'q'], ['լ', 'l'], ['հ', 'h'], ['յ', 'j'], ['ո', 'n'], ['ս', 'u'], ['օ', 'o'],
]);

/*
 * Georgien, ethiopien, hebreu, thai, ecritures indiennes, birman : les ensembles `kWholeScriptConfusables` de Chromium
 * tels quels. Chromium ne dit pas QUELLE lettre latine chacun imite ; le sosie nomme ici est celui de la table UTS #39
 * (`confusables.txt`), ramene en minuscule (un nom d'hote n'en a pas d'autre), et `null` quand cette table ne le ramene
 * pas a une lettre ou un chiffre latin : le caractere rend l'adresse trompeuse, mais le site imite n'est pas nomme.
 */

/** Lettres georgiennes qui ont un sosie latin. */
const GEORGIAN_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['ი', null], ['ო', null], ['ყ', 'y'], ['ძ', null], ['ხ', null], ['ჽ', 's'], ['ჿ', 'o'],
]);

/** Syllabes ethiopiennes qui ont un sosie latin (Chromium ne retient que les plus nettes, pour eviter les faux positifs). */
const ETHIOPIC_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['ሀ', 'u'], ['ሠ', null], ['ሰ', null], ['ስ', null], ['በ', null], ['ነ', null], ['ተ', null], ['ከ', null], ['ዐ', 'o'],
  ['ዕ', null], ['ዘ', null], ['ጠ', null], ['ፐ', null], ['ꬅ', null],
]);

/** Caracteres hebreux qui ont un sosie latin, ponctuation `׳` (geresh) comprise. */
const HEBREW_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['ד', null], ['ו', 'l'], ['ח', null], ['י', null], ['ן', 'l'], ['ס', 'o'], ['װ', 'll'], ['ײ', null], ['׳', null], ['ﬦ', null],
]);

/**
 * Caracteres thais qui ont un sosie latin, chiffre `๐` compris. Chromium en retient cinq partout et neuf de plus sous
 * Linux, ou la police les rend latins : le coffre est synchronise d'un appareil a l'autre, une adresse enregistree
 * ailleurs peut etre remplie sous Linux, l'ensemble large est donc retenu.
 */
const THAI_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['บ', null], ['พ', null], ['เ', null], ['แ', null], ['๐', 'o'],
  ['ท', null], ['น', null], ['ร', null], ['ห', null], ['ด', null], ['ล', null], ['ป', null], ['ฟ', null], ['ม', null],
]);

/**
 * Ecritures indiennes : chiffres et lettres qui ont un sosie latin (`০` bengali, `०` devanagari, `ഠ` malayalam...).
 * Chromium ne leur reconnait AUCUNE zone, pas meme `.in` : seul un domaine de premier niveau ecrit dans l'ecriture
 * (`.भारत`) en tient lieu.
 */
const BENGALI_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['০', 'o'], ['৭', '9']]);
const DEVANAGARI_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['ऽ', null], ['०', 'o'], ['ॱ', null]]);
const GUJARATI_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['ડ', null], ['ટ', null], ['૦', 'o'], ['૧', null]]);
const GURMUKHI_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['੦', 'o'], ['੧', '9']]);
const KANNADA_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['ಽ', null], ['೦', 'o'], ['೧', null]]);
const MALAYALAM_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['ട', 's'], ['ഠ', 'o'], ['ധ', null], ['ന', null], ['റ', null], ['൦', 'o'],
]);
const ORIYA_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['ଠ', 'o'], ['୦', 'o'], ['୮', null]]);
const TAMIL_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['ட', null], ['ப', null], ['௦', 'o']]);
const TELUGU_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([['౦', 'o'], ['౧', null]]);

/** Caracteres birmans qui ont un sosie latin, chiffres birmans et chiffres shan (`႐`...`႗`) compris. */
const MYANMAR_LATIN_ALIKE: ReadonlyMap<string, string | null> = new Map([
  ['ခ', null], ['ဂ', null], ['င', 'c'], ['ထ', null], ['ပ', null], ['ဝ', 'o'], ['၀', 'o'], ['၂', null], ['ၔ', null], ['ၜ', null],
  ['\u1090', null], ['\u1091', null], ['\u1095', null], ['\u1096', null], ['\u1097', null],
]);

/**
 * Ecritures dont un label entierement fait de sosies imite un nom latin, avec les domaines de premier niveau ou cette
 * ecriture est attendue (Chromium : `gr` pour le grec, `am` pour l'armenien, `ge`, `er` / `et`, `il`, `th`, `mm`, aucun
 * pour les ecritures indiennes). Un domaine de premier niveau ecrit dans l'ecriture elle-meme (`.рф`, `.ελ`, `.հայ`)
 * vaut zone.
 */
const WHOLE_SCRIPT_CONFUSABLES: ReadonlyMap<string, { readonly latinAlike: ReadonlyMap<string, string | null>; readonly tlds: ReadonlySet<string> }> = new Map<string, { readonly latinAlike: ReadonlyMap<string, string | null>; readonly tlds: ReadonlySet<string> }>([
  ['cyrillic', { latinAlike: CYRILLIC_LATIN_ALIKE, tlds: CYRILLIC_TLDS }],
  ['greek', { latinAlike: GREEK_LATIN_ALIKE, tlds: new Set(['gr']) }],
  ['armenian', { latinAlike: ARMENIAN_LATIN_ALIKE, tlds: new Set(['am']) }],
  ['georgian', { latinAlike: GEORGIAN_LATIN_ALIKE, tlds: new Set(['ge']) }],
  ['ethiopic', { latinAlike: ETHIOPIC_LATIN_ALIKE, tlds: new Set(['er', 'et']) }],
  ['hebrew', { latinAlike: HEBREW_LATIN_ALIKE, tlds: new Set(['il']) }],
  ['thai', { latinAlike: THAI_LATIN_ALIKE, tlds: new Set(['th']) }],
  ['bengali', { latinAlike: BENGALI_LATIN_ALIKE, tlds: new Set() }],
  ['devanagari', { latinAlike: DEVANAGARI_LATIN_ALIKE, tlds: new Set() }],
  ['gujarati', { latinAlike: GUJARATI_LATIN_ALIKE, tlds: new Set() }],
  ['gurmukhi', { latinAlike: GURMUKHI_LATIN_ALIKE, tlds: new Set() }],
  ['kannada', { latinAlike: KANNADA_LATIN_ALIKE, tlds: new Set() }],
  ['malayalam', { latinAlike: MALAYALAM_LATIN_ALIKE, tlds: new Set() }],
  ['oriya', { latinAlike: ORIYA_LATIN_ALIKE, tlds: new Set() }],
  ['tamil', { latinAlike: TAMIL_LATIN_ALIKE, tlds: new Set() }],
  ['telugu', { latinAlike: TELUGU_LATIN_ALIKE, tlds: new Set() }],
  ['myanmar', { latinAlike: MYANMAR_LATIN_ALIKE, tlds: new Set(['mm']) }],
]);

/**
 * Caracteres qui imitent un chiffre, avec le chiffre imite : `digit_lookalikes_` de Chromium (`idn_spoof_checker.cc`) tel
 * quel, chacun associe au chiffre que lui donne `extra_confusable_mapper_` (`skeleton_generator.cc`).
 */
const DIGIT_ALIKE: ReadonlyMap<string, string> = new Map([
  ['θ', '0'],
  ['२', '2'], ['২', '2'], ['੨', '2'], ['૨', '2'], ['೩', '2'], ['೭', '2'], ['շ', '2'],
  ['з', '3'], ['ҙ', '3'], ['ӡ', '3'], ['उ', '3'], ['ও', '3'], ['ਤ', '3'], ['੩', '3'], ['૩', '3'], ['౩', '3'], ['ဒ', '3'],
  ['ვ', '3'], ['პ', '3'], ['ੜ', '3'], ['კ', '3'],
  ['੫', '4'], ['丩', '4'], ['ㄐ', '4'],
  ['ճ', '6'],
  ['৪', '8'], ['੪', '8'], ['୫', '8'],
  ['૭', '9'], ['୨', '9'], ['౨', '9'],
]);

/** Un label fait de chiffres ASCII et d'au moins un sosie de chiffre (Chromium : `IsDigitLookalike`). */
function isDigitLookalike(label: string): boolean {
  let hasLookalike = false;
  for (const char of label) {
    if (char >= '0' && char <= '9') continue;
    if (!DIGIT_ALIKE.has(char)) return false;
    hasLookalike = true;
  }
  return hasLookalike;
}

/**
 * Un caractere que Chromium ne laisse qu'a une zone (`kTLDSpecificCharacters`) : `þ` (imite b et p) et `ð` (imite o)
 * hors `.is` et `.fo`, le schwa `ə` hors `.az`. Un label d'un seul caractere (en UTF-16, comme ICU) n'est pas vise.
 */
function hasZoneCharacter(label: string, tld: string): boolean {
  if (label.length <= 1) return false;
  if (tld !== 'is' && tld !== 'fo' && /[\u00fe\u00f0]/u.test(label)) return true;
  return tld !== 'az' && label.includes('\u0259');
}

/**
 * Le point median `·` (U+00B7) hors de son seul usage admis : sous `.cat`, entre deux `l` (ela geminada, RFC 5892
 * annexe A.3). Chromium : `HasUnsafeMiddleDot`.
 */
function hasUnsafeMiddleDot(label: string, tld: string): boolean {
  for (let index = label.indexOf('\u00b7'); index !== -1; index = label.indexOf('\u00b7', index + 1)) {
    if (tld !== 'cat' || label[index - 1] !== 'l' || label[index + 1] !== 'l') return true;
  }
  return false;
}

/** Les plages d'UTS #39 (`Recommended` + `Inclusion`), triees : celles qu'ICU rend a Chromium. */
const IDN_ALLOWED: readonly (readonly [number, number])[] = IDN_ALLOWED_RANGES.split(' ').map((range) => {
  const [from, to = from] = range.split('-');
  return [parseInt(from!, 16), parseInt(to!, 16)] as const;
});

/**
 * Ce que Chromium retire de ce jeu (`SetAllowedUnicodeSet`, `idn_spoof_checker.cc`, `main` du 2026-10-03), dans son ordre.
 * Les retraits propres a macOS (polices qui affichent ces caracteres en blanc) sont gardes partout : le coffre est
 * synchronise, une adresse enregistree ici peut etre remplie sur un Mac.
 */
const CHROMIUM_REMOVED: readonly (readonly [number, number])[] = [
  [0x0338, 0x0338], // barre oblique longue combinante : une barre sous une police defaillante
  [0x058a, 0x058a], // trait d'union armenien (NV8, invalide en IDNA 2008)
  [0x2010, 0x2010], // trait d'union : imite le tiret ASCII
  [0x2019, 0x2019], // apostrophe droite
  [0x2027, 0x2027], // point de coupure : imite le point median katakana
  [0x30a0, 0x30a0], // double trait d'union kana
  [0x02bb, 0x02bc], // virgule culbutee et apostrophe modificatives : des guillemets
  [0x02ec, 0x02ec], // voisement modificatif
  [0x0138, 0x0138], // kra
  [0x0620, 0x0620], // yeh cachemiri (macOS)
  [0x0f8c, 0x0f8f], // translitteration tibetaine (macOS)
  [0x01cd, 0x01dc], // latin etendu B : pinyin
  [0x1c80, 0x1c8f], // cyrillique etendu C
  [0x1e00, 0x1e9b], // latin etendu additionnel
  [0x1f00, 0x1fff], // grec etendu
  [0xa640, 0xa69f], // cyrillique etendu B
  [0xa720, 0xa7ff], // latin etendu D
];

function inRanges(ranges: readonly (readonly [number, number])[], code: number): boolean {
  let low = 0;
  let high = ranges.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const [from, to] = ranges[middle]!;
    if (code < from) high = middle - 1;
    else if (code > to) low = middle + 1;
    else return true;
  }
  return false;
}

/** Un caractere hors du jeu admis par Chromium (`USPOOF_CHAR_LIMIT`) ? */
function hasDisallowedCharacter(label: string): boolean {
  for (const char of label) {
    const code = char.codePointAt(0)!;
    if (!inRanges(IDN_ALLOWED, code) || CHROMIUM_REMOVED.some(([from, to]) => code >= from && code <= to)) return true;
  }
  return false;
}

/**
 * Squelettes d'un nom (Chromium : `SkeletonGenerator::GetSkeletons`, `skeleton_generator.cc`) : les accents otes si le nom
 * n'est que latin, grec, cyrillique, chiffres et `.-_` ; une variante par lecture des lettres a double sosie (`þ` -> b/p,
 * `ł` -> l/t...) ; les sosies propres a Chromium (`extra_confusable_mapper_`) ; puis le squelette d'ICU
 * (`uspoof_getSkeletonUnicodeString` : NFD, table `MA` d'UTS #39, NFD). La table embarquee ne garde que les cibles ASCII :
 * un squelette qui garde un caractere non ASCII est juste, mais ne peut egaler aucun domaine de la liste.
 */
const LGC_LETTERS_N_ASCII = /^[\p{Script=Latin}\p{Script=Greek}\p{Script=Cyrillic}0-9._\-̀-̹]*$/u;
const DIACRITIC_FOLD: ReadonlyMap<string, string> = new Map([['ł', 'l'], ['ø', 'o'], ['đ', 'd']]);
const EXTRA_CONFUSABLES: ReadonlyMap<string, string> = new Map(
  (
    [
      ['æӕ', 'ae'], ['ϼҏ', 'p'], ['ħнћңҥӈӊԋԧԩ', 'h'], ['ĸκкқҝҟҡӄԟ', 'k'], ['ŋпԥกח', 'n'], ['ŧтҭԏ七丅丆丁', 't'],
      ['ƅьҍвß', 'b'], ['ωшщพฟພຟ', 'w'], ['мӎ', 'm'], ['єҽҿၔ', 'e'], ['ґ', 'r'], ['ғӻ', 'f'], ['ҫင', 'c'], ['ұ丫', 'y'],
      ['χҳӽӿ乂', 'x'], ['ԃძ', 'd'], ['ԍဌ', 'g'], ['ടรຣຮ', 's'], ['၂', 'j'], ['०০੦૦ଠ୦೦စ', 'o'], ['৭੧૧', 'q'], ['บບ', 'u'],
      ['θ', '0'], ['२২੨૨೩೭շ', '2'], ['зҙӡउওਤ੩૩౩ဒვპੜკ', '3'], ['੫丩ㄐ', '4'], ['ճ', '6'], ['৪੪୫', '8'], ['૭୨౨', '9'],
      ['—一―⸺⸻', '-'],
    ] as const
  ).flatMap(([chars, target]) => [...chars].map((char) => [char, target] as const)),
);
const MULTIPLE_SKELETONS: ReadonlyMap<string, readonly string[]> = new Map([
  ['þ', ['b', 'p']], ['œ', ['ce', 'oe']], ['ł', ['l', 't']], ['ı', ['l', 'i']],
]);
const ICU_ASCII_CONFUSABLES: ReadonlyMap<number, string> = new Map(
  IDN_CONFUSABLES_ASCII.split(' ').map((entry) => {
    const [from, to] = entry.split('=');
    return [parseInt(from!, 16), String.fromCodePoint(...to!.split(',').map((code) => parseInt(code, 16)))] as const;
  }),
);

function withoutTrailingDot(hostname: string): string {
  return hostname.endsWith('.') ? hostname.slice(0, -1) : hostname;
}

function maybeRemoveDiacritics(hostname: string): string {
  const host = withoutTrailingDot(hostname);
  if (!LGC_LETTERS_N_ASCII.test(host)) return host;
  return [...host.normalize('NFD').replace(/\p{Mn}/gu, '').normalize('NFC')].map((char) => DIACRITIC_FOLD.get(char) ?? char).join('');
}

/** Les variantes d'un nom par lecture des lettres a double sosie, dans l'ordre de parcours de Chromium (128 au plus). */
function supplementalHostnames(input: string): string[] {
  // Chromium compte en unites UTF-16 et renonce au-dela de 32, ou au-dela de 5 lettres a double sosie : il ne rend alors
  // AUCUN nom et ne compare plus rien (budget d'un navigateur). Ecart voulu : ici le nom lui-meme reste compare, sans
  // variantes -- `connexion.securite.comptes.góogle.com` ne s'echappe pas par sa longueur
  if (input.length === 0) return [];
  const chars = [...input];
  if (input.length > 32 || chars.filter((char) => MULTIPLE_SKELETONS.has(char)).length > 5) return [input];
  const output = new Set<string>();
  const queue: string[][] = [[]];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.length === chars.length) {
      output.add(current.join(''));
      if (output.size === 128) break;
      continue;
    }
    const char = chars[current.length]!;
    queue.push([...current, char]);
    for (const alternative of MULTIPLE_SKELETONS.get(char) ?? []) queue.push([...current, alternative]);
  }
  return [...output];
}

/** Les lettres a double sosie que l'effacement des accents change (`ł`) : leurs variantes se lisent aussi sur le nom accentue. */
const MULTIPLE_SKELETONS_WITH_DIACRITICS = new Set([...MULTIPLE_SKELETONS.keys()].filter((char) => maybeRemoveDiacritics(char) !== char));

function icuSkeleton(text: string): string {
  return [...text.normalize('NFD')].map((char) => ICU_ASCII_CONFUSABLES.get(char.codePointAt(0)!) ?? char).join('').normalize('NFD');
}

export function hostSkeletons(hostname: string): string[] {
  const variants = new Set(supplementalHostnames(maybeRemoveDiacritics(hostname)));
  if ([...hostname].some((char) => MULTIPLE_SKELETONS_WITH_DIACRITICS.has(char))) {
    for (const variant of supplementalHostnames(hostname)) variants.add(variant);
  }
  const skeletons = new Set<string>();
  for (const variant of variants) {
    const transliterated = [...withoutTrailingDot(variant)].map((char) => EXTRA_CONFUSABLES.get(char) ?? char).join('');
    // Chromium ajoute un squelette ou le palochka `ӏ` devient `l` (`AddSkeletonMapping`) : la table d'Unicode 17.0 l'y
    // mappe deja, le pas ne changerait aucun squelette (aucun cas ne le distingue) et n'est donc pas repris
    skeletons.add(icuSkeleton(transliterated));
  }
  // `Skeletons` est un ensemble trie (`base::flat_set`) : le premier squelette qui trouve un domaine l'emporte
  return [...skeletons].sort();
}

/** Des domaines, par squelette (le premier domaine d'un squelette l'emporte, comme dans le trie de Chromium). */
export function skeletonIndex(domains: Iterable<string>): ReadonlyMap<string, string> {
  const bySkeleton = new Map<string, string>();
  for (const domain of domains) {
    for (const skeleton of hostSkeletons(domain)) if (!bySkeleton.has(skeleton)) bySkeleton.set(skeleton, domain);
  }
  return bySkeleton;
}

let topDomainsBySkeleton: ReadonlyMap<string, string> | null = null;

/** Les domaines les plus visites selon Chromium, par squelette ; calcule au premier besoin. */
export function topDomainSkeletons(): ReadonlyMap<string, string> {
  topDomainsBySkeleton ??= skeletonIndex(IDN_TOP_DOMAINS.split(' '));
  return topDomainsBySkeleton;
}

/**
 * Le domaine tres visite dont ce nom (decode) a le squelette (Chromium : `GetSimilarTopDomain`) ; `null` s'il n'y en a
 * pas, ou si le nom EST ce domaine ou l'un de ses sous-domaines (`éxample.blogspot.com` n'imite pas blogspot.com).
 */
export function similarTopDomain(hostname: string, bySkeleton: ReadonlyMap<string, string> = topDomainSkeletons()): string | null {
  for (const skeleton of hostSkeletons(hostname)) {
    // les quatre derniers labels au plus, puis de moins en moins ; un squelette entier a au moins deux labels
    const labels = skeleton.split('.').slice(-4);
    for (; labels.length >= 2; labels.shift()) {
      const domain = bySkeleton.get(labels.join('.'));
      if (domain === undefined) continue;
      const lower = hostname.toLowerCase();
      return lower === domain || lower.endsWith(`.${domain}`) ? null : domain;
    }
  }
  return null;
}

/** Les kana qui obligent a passer par les motifs dangereux meme dans une seule ecriture (`kana_letters_exceptions_`). */
const KANA_LETTER_EXCEPTIONS = /[\u3078-\u307a\u30d8-\u30da\u30fb-\u30fe]/u;
/** Les accents combinants qui y obligent aussi (`combining_diacritics_exceptions_`). */
const COMBINING_DIACRITICS = /[\u0300-\u0339]/u;

/** `[^...]` d'une liste d'ecritures (`Script_Extensions`), comme les classes ICU de Chromium. */
const NOT_SCX = (...scripts: string[]): string => `[^${scripts.map((script) => `\\p{scx=${script}}`).join('')}]`;
const SLASH_ALIKE = '[\u30ce\u30f3\u30bd\u30be\u4e36\u4e40\u4e41\u4e3f]';
const CJK_LATIN_ALIKE =
  '[\u4e00\u3127\u4e28\u4e5b\u4e03\u4e05\u5341\u3007\u3112\u311a\u311f\u3128\u3129\u3108\u31ba\u31b3\u5de5\u31b2\u8ba0\u4e01\u4e36\u4e85\u4e8c\u4ea0\u5196\u5b80\u5ddb]';

/**
 * Les motifs dangereux de Chromium (`dangerous_pattern`, `idn_spoof_checker.cc`, `main` du 2026-10-03), alternative par
 * alternative et dans le meme ordre.
 */
const DANGEROUS_PATTERN = new RegExp(
  [
    // ノ ン ソ ゾ et les traits han qui imitent une barre, entre deux caracteres qui ne sont pas japonais ou chinois
    `${NOT_SCX('Kana', 'Hira', 'Hani')}${SLASH_ALIKE}${NOT_SCX('Kana', 'Hira', 'Hani')}`,
    // ヽ ヾ (marques d'iteration) ailleurs qu'apres un katakana
    `${NOT_SCX('Kana')}[\u30fd\u30fe]`, '^[\u30fd\u30fe]',
    // へ べ ぺ dans un label tout katakana, ヘ ベ ペ dans un label tout hiragana : identiques a l'oeil
    '^\\p{scx=Kana}+[\u3078-\u307a]\\p{scx=Kana}+$', '^\\p{scx=Hira}+[\u30d8-\u30da]\\p{scx=Hira}+$',
    // ー (trait d'allongement) ailleurs qu'apres un kana ; ・ (point median) hors kana et han, ou en fin de label
    `${NOT_SCX('Kana', 'Hira')}\u30fc`, '^\u30fc',
    `${NOT_SCX('Kana', 'Hira', 'Hani')}\u30fb`, `\u30fb${NOT_SCX('Kana', 'Hira', 'Hani')}`, '\u30fb$',
    // ideogrammes et radicaux qui imitent une lettre latine ou une ponctuation (一 ㄧ 丨 七 十 〇 工...), a cote du non-CJK
    `${NOT_SCX('Kana', 'Hira', 'Hani', 'Bopo')}${CJK_LATIN_ALIKE}`, `${CJK_LATIN_ALIKE}${NOT_SCX('Kana', 'Hira', 'Hani', 'Bopo')}`,
    // accent combinant apres ce qui n'est pas latin, grec ou cyrillique ; apres le i sans point
    `${NOT_SCX('Latn', 'Grek', 'Cyrl')}[\u0300-\u0339]`, '\u0131[\u0300-\u0339]',
    // marques de voisement kana combinantes
    '\u3099', '\u309a',
    // point suscrit sur i, j, l (un second point sur le i)
    '[ijl]\u0307',
  ].join('|'),
  'u',
);

/** Le sosie latin d'une lettre, toutes ecritures confondues (pour nommer le site imite). */
const LATIN_ALIKE: ReadonlyMap<string, string> = new Map(
  [
    ...CYRILLIC_LATIN_ALIKE, ...GREEK_LATIN_ALIKE, ...ARMENIAN_LATIN_ALIKE, ...GEORGIAN_LATIN_ALIKE, ...ETHIOPIC_LATIN_ALIKE, ...HEBREW_LATIN_ALIKE, ...THAI_LATIN_ALIKE,
    ...BENGALI_LATIN_ALIKE, ...DEVANAGARI_LATIN_ALIKE, ...GUJARATI_LATIN_ALIKE, ...GURMUKHI_LATIN_ALIKE, ...KANNADA_LATIN_ALIKE, ...MALAYALAM_LATIN_ALIKE,
    ...ORIYA_LATIN_ALIKE, ...TAMIL_LATIN_ALIKE, ...TELUGU_LATIN_ALIKE, ...MYANMAR_LATIN_ALIKE,
  ].filter(
    (pair): pair is [string, string] => pair[1] !== null,
  ),
);

let topBucketSeparatorsRemoved: ReadonlyMap<string, string> | null = null;

/**
 * Les domaines du top bucket de Chromium par squelette SANS separateurs (`googlecom` -> google.com) ; calcule au premier
 * besoin. Le premier domaine d'un squelette l'emporte.
 */
export function topBucketSeparatorsRemovedSkeletons(): ReadonlyMap<string, string> {
  if (topBucketSeparatorsRemoved === null) {
    const bySkeleton = new Map<string, string>();
    for (const domain of IDN_TOP_DOMAINS.split(' ').slice(0, IDN_TOP_BUCKET_COUNT)) {
      for (const skeleton of hostSkeletons(domain)) {
        const key = skeleton.replace(/[.-]/gu, '');
        if (!bySkeleton.has(key)) bySkeleton.set(key, domain);
      }
    }
    topBucketSeparatorsRemoved = bySkeleton;
  }
  return topBucketSeparatorsRemoved;
}

let topBucketDomains: ReadonlySet<string> | null = null;

function isTopBucket(domain: string): boolean {
  topBucketDomains ??= new Set(IDN_TOP_DOMAINS.split(' ').slice(0, IDN_TOP_BUCKET_COUNT));
  return topBucketDomains.has(domain);
}

interface TopDomainEntry {
  readonly domain: string;
  readonly separatorsRemoved: boolean;
}

/**
 * Chromium : `LookupSkeletonInTopDomains` -- les quatre derniers labels du squelette au plus, puis de moins en moins ; un
 * squelette entier a au moins deux labels, un squelette sans separateurs n'en a qu'un.
 */
function lookupSkeletonInTopDomains(skeleton: string, separatorsRemoved: boolean): TopDomainEntry | null {
  const labels = skeleton.split('.').slice(-4);
  for (; labels.length > 0; labels.shift()) {
    if (labels.length === 1 && !separatorsRemoved) break;
    const key = labels.join('.');
    const full = topDomainSkeletons().get(key);
    if (full !== undefined) return { domain: full, separatorsRemoved: false };
    const withoutSeparators = topBucketSeparatorsRemovedSkeletons().get(key);
    if (withoutSeparators !== undefined) return { domain: withoutSeparators, separatorsRemoved: true };
  }
  return null;
}

/** Longueur d'un nom en octets UTF-8, comme la `std::string` qu'en mesure Chromium. */
function byteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/** Nom d'hote decode (unicode) ramene a l'ASCII que lit la Public Suffix List ; `null` s'il n'en est pas un. */
function asciiHostname(hostname: string): string | null {
  try {
    return new URL(`http://${hostname}`).hostname;
  } catch {
    return null;
  }
}

function unicodeHostname(ascii: string): string {
  return ascii
    .split('.')
    .map((label) => (label.startsWith('xn--') ? (decodePunycode(label.slice(4)) ?? label) : label))
    .join('.');
}

/** Chromium : `kPrivateRegistriesTreatedAsPublic` -- registres prives qui se comportent en publics. */
const PRIVATE_REGISTRIES_TREATED_AS_PUBLIC: readonly string[] = ['com.de', 'com.se'];

/** Chromium : `lookalikes::GetETLDPlusOne` -- registres publics seulement, sauf ceux qui se comportent en publics. */
function etldPlusOne(ascii: string): string {
  const pub = knownRegistrableDomain(ascii, false) ?? '';
  const priv = knownRegistrableDomain(ascii, true) ?? '';
  if (pub === priv) return pub;
  return PRIVATE_REGISTRIES_TREATED_AS_PUBLIC.includes(pub) ? priv : pub;
}

/** Longueur du registre d'un domaine (`co.uk` dans `example.co.uk`) ; 0 si le domaine est lui-meme un registre. */
function registryLength(domain: string, privateRules: boolean): number {
  const registrable = knownRegistrableDomain(domain, privateRules);
  return registrable === null ? 0 : registrable.length - registrable.indexOf('.') - 1;
}

/** Le domaine sans son registre ni ses points de bord (`example.co.uk` -> `example`). */
function withoutRegistry(domain: string, length: number): string {
  return domain.slice(0, domain.length - length).replace(/^\.+|\.+$/gu, '');
}

/** Chromium : `GetE2LDWithDeFactoPublicRegistries` -- `google.com.de` -> `google`. */
function e2ldWithDeFactoPublicRegistries(domainAndRegistry: string): string {
  if (domainAndRegistry === '') return '';
  const ascii = asciiHostname(domainAndRegistry) ?? domainAndRegistry;
  let length = registryLength(ascii, false);
  const privateLength = registryLength(ascii, true);
  if (length !== privateLength && PRIVATE_REGISTRIES_TREATED_AS_PUBLIC.some((registry) => ascii.endsWith(registry))) length = privateLength;
  return unicodeHostname(withoutRegistry(ascii, length));
}

/** Chromium : `DomainInfo` -- un nom, son domaine enregistrable, ce domaine sans registre, et ses squelettes. */
interface DomainInfo {
  readonly hostname: string;
  readonly domainAndRegistry: string;
  readonly domainWithoutRegistry: string;
  /** Les memes en ASCII (punycode), la forme que mesurent et comparent les garde-fous de la distance d'edition. */
  readonly asciiDomainAndRegistry: string;
  readonly asciiDomainWithoutRegistry: string;
  readonly skeletons: ReadonlySet<string>;
  /** Les squelettes du domaine sans son registre (`google-login` pour google-login.com), que compare le combosquatting. */
  readonly domainWithoutRegistrySkeletons: ReadonlySet<string>;
}

/** Chromium : `GetDomainInfo`. Les noms sont rendus decodes ; les squelettes, ceux du domaine enregistrable. */
function domainInfo(hostname: string): DomainInfo {
  const ascii = asciiHostname(hostname);
  const domain = ascii === null ? '' : etldPlusOne(ascii);
  if (ascii === null || domain === '') {
    return {
      hostname,
      domainAndRegistry: '',
      domainWithoutRegistry: '',
      asciiDomainAndRegistry: '',
      asciiDomainWithoutRegistry: '',
      skeletons: new Set(),
      domainWithoutRegistrySkeletons: new Set(),
    };
  }
  const domainAndRegistry = unicodeHostname(domain);
  const asciiDomainWithoutRegistry = withoutRegistry(domain, registryLength(domain, false));
  const domainWithoutRegistry = unicodeHostname(asciiDomainWithoutRegistry);
  let skeletons: ReadonlySet<string> | null = null;
  let domainWithoutRegistrySkeletons: ReadonlySet<string> | null = null;
  return {
    hostname: unicodeHostname(ascii),
    domainAndRegistry,
    domainWithoutRegistry,
    asciiDomainAndRegistry: domain,
    asciiDomainWithoutRegistry,
    // a la demande : le balayage de l'enchassement construit bien plus de noms qu'il n'en compare par squelette
    get skeletons() {
      skeletons ??= new Set(hostSkeletons(domainAndRegistry));
      return skeletons;
    },
    get domainWithoutRegistrySkeletons() {
      domainWithoutRegistrySkeletons ??= new Set(hostSkeletons(domainWithoutRegistry));
      return domainWithoutRegistrySkeletons;
    },
  };
}

/**
 * Les sites du coffre, cibles protegees de l'enchassement (Chromium : les sites « engages », que l'utilisateur
 * frequente), indexes pour qu'un coffre de milliers d'entrees ne compare pas chaque nom a chaque site. Construits par
 * `engagedSites`.
 */
export interface EngagedSites {
  /** Par nom d'hote decode (un site par nom). */
  readonly byHostname: ReadonlyMap<string, DomainInfo>;
  /** Les sites nus ou en `www.`, par squelette de leur domaine enregistrable, dans l'ordre ; calcule au premier besoin. */
  bySkeleton(): ReadonlyMap<string, readonly DomainInfo[]>;
  /** Tous les sites, sous-domaines compris, par squelette de leur domaine enregistrable ; calcule au premier besoin. */
  byDomainSkeleton(): ReadonlyMap<string, readonly DomainInfo[]>;
  /**
   * Les sites comparables a une lettre pres (`IsEditDistanceCandidate`), dans l'ordre, et leurs rangs par longueur de
   * squelette : seuls ceux dont un squelette a la longueur du nom juge a un pres peuvent lui ressembler ; calcule au
   * premier besoin.
   */
  byEditDistanceLength(): { readonly sites: readonly DomainInfo[]; readonly byLength: ReadonlyMap<number, readonly number[]> };
  /**
   * Chromium : `GetBrandNamesFromEngagedSites` -- les noms hors registre (ASCII) de plus de 4 lettres et chacun de leurs
   * squelettes, dans l'ordre du coffre, sans doublon ; calcule au premier besoin.
   */
  comboSquattingBrands(): readonly (readonly [string, string])[];
}

/** Les sites (noms d'hote, punycode ou decodes) dont l'enchassement est vise ; sans doublon, sans nom hors registre. */
export function engagedSites(hostnames: Iterable<string>): EngagedSites {
  const byHostname = new Map<string, DomainInfo>();
  for (const hostname of hostnames) {
    const site = domainInfo(hostname);
    if (site.domainAndRegistry !== '' && !byHostname.has(site.hostname)) byHostname.set(site.hostname, site);
  }
  const index = (keep: (site: DomainInfo) => boolean): Map<string, DomainInfo[]> => {
    const bySkeleton = new Map<string, DomainInfo[]>();
    for (const site of byHostname.values()) {
      if (!keep(site)) continue;
      for (const skeleton of site.skeletons) {
        const list = bySkeleton.get(skeleton);
        if (list === undefined) bySkeleton.set(skeleton, [site]);
        else list.push(site);
      }
    }
    return bySkeleton;
  };
  let bySkeleton: ReadonlyMap<string, readonly DomainInfo[]> | null = null;
  let byDomainSkeleton: ReadonlyMap<string, readonly DomainInfo[]> | null = null;
  let byEditDistanceLength: ReturnType<EngagedSites['byEditDistanceLength']> | null = null;
  let comboSquattingBrands: (readonly [string, string])[] | null = null;
  return {
    byHostname,
    bySkeleton() {
      bySkeleton ??= index(isEtldPlusOneOrTrivialSubdomain);
      return bySkeleton;
    },
    byDomainSkeleton() {
      byDomainSkeleton ??= index(() => true);
      return byDomainSkeleton;
    },
    byEditDistanceLength() {
      if (byEditDistanceLength === null) {
        const sites = [...byHostname.values()].filter(isEditDistanceCandidate);
        const byLength = new Map<number, number[]>();
        for (const [rank, site] of sites.entries()) {
          for (const length of new Set([...site.skeletons].map((skeleton) => skeleton.length))) {
            const ranks = byLength.get(length);
            if (ranks === undefined) byLength.set(length, [rank]);
            else ranks.push(rank);
          }
        }
        byEditDistanceLength = { sites, byLength };
      }
      return byEditDistanceLength;
    },
    comboSquattingBrands() {
      if (comboSquattingBrands === null) {
        const seen = new Set<string>();
        comboSquattingBrands = [];
        for (const site of byHostname.values()) {
          if (!isComboSquattingCandidate(site.asciiDomainWithoutRegistry)) continue;
          for (const skeleton of site.domainWithoutRegistrySkeletons) {
            // un doublon arrive apres son original : il ne peut rien trouver que l'original n'ait trouve
            const key = `${site.asciiDomainWithoutRegistry} ${skeleton}`;
            if (seen.has(key)) continue;
            seen.add(key);
            comboSquattingBrands.push([site.asciiDomainWithoutRegistry, skeleton]);
          }
        }
      }
      return comboSquattingBrands;
    },
  };
}

/**
 * Le site du coffre que ce nom (decode) imite a un accent ou un sosie pres (`mabanqué.fr` -> mabanque.fr) : Chromium,
 * `GetMatchingSiteEngagementDomain` -- un squelette commun entre les domaines enregistrables, cherche seulement quand
 * celui du nom est internationalise (`has_idn_component` : `mail.sité1.com` oui, `xn--sit-bpa.example.com` non), le
 * premier site du coffre dans l'ordre l'emportant. `null` s'il n'y en a pas.
 *
 * Comme pour l'enchassement, un site du coffre est une cible, jamais une excuse (chez Chromium, un site frequente n'est
 * pas juge) : seuls ceux du meme domaine enregistrable que le nom sont ecartes.
 */
export function similarVaultSite(hostname: string, sites: EngagedSites): string | null {
  if (sites.byHostname.size === 0) return null;
  const navigated = domainInfo(hostname);
  if (!/[^\x00-\x7f]/u.test(navigated.domainAndRegistry)) return null;
  const matched = new Set<DomainInfo>();
  for (const skeleton of navigated.skeletons) {
    for (const site of sites.byDomainSkeleton().get(skeleton) ?? []) {
      if (site.domainAndRegistry !== navigated.domainAndRegistry) matched.add(site);
    }
  }
  if (matched.size === 0) return null;
  for (const site of sites.byHostname.values()) if (matched.has(site)) return site.domainAndRegistry;
  return null;
}

const NO_SITES: EngagedSites = engagedSites([]);

/** Chromium : `kMinLengthForEditDistance` -- un domaine de moins de 5 lettres hors registre (`abc.co.uk`) n'est pas compare. */
const MIN_LENGTH_FOR_EDIT_DISTANCE = 5;
/** Chromium : `kMaxDomains` de `make_top_domain_list_variables` -- les 500 premiers squelettes comparables du top bucket. */
const MAX_TOP_BUCKET_EDIT_DISTANCE_SKELETONS = 500;

/** Chromium : `IsEditDistanceCandidate` -- sur le domaine enregistrable ASCII, sans son registre. */
function isEditDistanceCandidate(domain: DomainInfo): boolean {
  return domain.asciiDomainAndRegistry !== '' && domain.asciiDomainWithoutRegistry.length >= MIN_LENGTH_FOR_EDIT_DISTANCE;
}

/** Chromium : `IsEditDistanceAtMostOne` -- une lettre ajoutee, otee ou changee au plus (unites UTF-16). */
export function isEditDistanceAtMostOne(str1: string, str2: string): boolean {
  if (str1.length > str2.length + 1 || str2.length > str1.length + 1) return false;
  let i = 0;
  let j = 0;
  let editCount = 0;
  while (i < str1.length && j < str2.length) {
    if (str1[i] === str2[j]) {
      i += 1;
      j += 1;
      continue;
    }
    editCount += 1;
    if (editCount > 1) return false;
    // la chaine la plus longue porte la lettre en trop ; a longueur egale, une lettre differe
    if (str1.length >= str2.length) i += 1;
    if (str2.length >= str1.length) j += 1;
  }
  // une lettre restee seule au bout
  if (i < str1.length || j < str2.length) editCount += 1;
  return editCount <= 1;
}

/** Chromium : `HasOneCharacterSwap` -- meme longueur, exactement deux lettres voisines interverties. */
export function hasOneCharacterSwap(str1: string, str2: string): boolean {
  if (str1.length !== str2.length || str1 === str2) return false;
  let hasSwap = false;
  for (let i = 0; i < str1.length; i += 1) {
    if (str1[i] === str2[i]) continue;
    if (!hasSwap && str1[i] === str2[i + 1] && str2[i] === str1[i + 1]) {
      hasSwap = true;
      i += 1;
      continue;
    }
    // plusieurs inversions, ou des lettres simplement differentes
    return false;
  }
  return hasSwap;
}

/** Chromium : `GetFirstDifferentChar`. */
function firstDifferentChar(str1: string, str2: string): string | undefined {
  for (let i = 0; i < str1.length && i < str2.length; i += 1) if (str1[i] !== str2[i]) return str1[i];
  return undefined;
}

/**
 * Chromium : `IsLikelyEditDistanceFalsePositive`, sur les formes ASCII -- seul le registre change (`google.com.tw`), un
 * suffixe numerique (`site45` / `site35`), la premiere lettre (`hank` / `tank`, `oodle` / `poodle`) ou un tiret de plus.
 */
function isLikelyEditDistanceFalsePositive(navigated: DomainInfo, matched: DomainInfo): boolean {
  if (navigated.asciiDomainWithoutRegistry === matched.asciiDomainWithoutRegistry) return true;
  if (navigated.asciiDomainWithoutRegistry.replace(/[0-9]+$/u, '') === matched.asciiDomainWithoutRegistry.replace(/[0-9]+$/u, '')) return true;
  const nav = navigated.asciiDomainAndRegistry;
  const target = matched.asciiDomainAndRegistry;
  if (nav.length === target.length && nav.slice(1) === target.slice(1)) return true;
  if (nav.length < target.length && nav === target.slice(1)) return true;
  if (nav.length > target.length && nav.slice(1) === target) return true;
  if (nav.length < target.length && firstDifferentChar(target, nav) === '-') return true;
  if (nav.length > target.length && firstDifferentChar(nav, target) === '-') return true;
  return false;
}

/** Chromium : `IsLikelyCharacterSwapFalsePositive` -- seul le registre change (`google.sr` / `google.rs`). */
function isLikelyCharacterSwapFalsePositive(navigated: DomainInfo, matched: DomainInfo): boolean {
  return navigated.asciiDomainWithoutRegistry === matched.asciiDomainWithoutRegistry;
}

let topBucketEditDistance: ReadonlyMap<number, readonly string[]> | null = null;

/**
 * Chromium : `kTopBucketEditDistanceSkeletons` (`make_top_domain_list_variables`) -- le squelette ICU des domaines
 * comparables du top bucket, dans l'ordre de la liste jusqu'a 500 squelettes distincts, puis tries ; calcule au premier
 * besoin.
 */
export function topBucketEditDistanceSkeletons(): readonly string[] {
  const skeletons = new Set<string>();
  for (const domain of IDN_TOP_DOMAINS.split(' ').slice(0, IDN_TOP_BUCKET_COUNT)) {
    if (skeletons.size >= MAX_TOP_BUCKET_EDIT_DISTANCE_SKELETONS) break;
    if (isEditDistanceCandidate(domainInfo(domain))) skeletons.add(icuSkeleton(domain));
  }
  return [...skeletons].sort();
}

export interface SimilarDomain {
  /** `edit-distance` : une lettre ajoutee, otee ou changee ; `character-swap` : deux lettres voisines interverties. */
  readonly kind: 'edit-distance' | 'character-swap';
  /** Le site imite (domaine enregistrable). */
  readonly target: string;
}

/**
 * Ecart voulu : un site du coffre internationalise qui a le squelette du nom juge (`mabanqué.fr` pour `mabanque.fr`) n'en
 * est pas la cible. Ce couple releve du controle des squelettes (`similarVaultSite`), qui juge le cote internationalise ;
 * Chromium, lui, previendrait aussi sur la vraie banque des que l'utilisateur a frequente le sosie.
 */
function ownedBySkeletonCheck(navigated: DomainInfo, site: DomainInfo): boolean {
  return /[^\x00-\x7f]/u.test(site.domainAndRegistry) && [...site.skeletons].some((skeleton) => navigated.skeletons.has(skeleton));
}

/**
 * Chromium : `GetSimilarDomainFromEngagedSites` -- les squelettes des domaines enregistrables a une lettre ou une
 * inversion pres, le premier site du coffre dans l'ordre l'emportant ; puis, pour un nom accentue, l'inversion sur les
 * noms sans accents (`m` a pour squelette `rn` : `exapmle` n'est pas une inversion d'`example` sur les squelettes).
 */
function similarDomainFromVaultSites(navigated: DomainInfo, sites: EngagedSites): SimilarDomain | null {
  const { sites: candidates, byLength } = sites.byEditDistanceLength();
  for (const skeleton of navigated.skeletons) {
    // les rangs des sites d'une longueur a un pres, marques puis parcourus dans l'ordre du coffre
    const marked = new Uint8Array(candidates.length);
    for (const length of [skeleton.length - 1, skeleton.length, skeleton.length + 1]) for (const rank of byLength.get(length) ?? []) marked[rank] = 1;
    for (let rank = 0; rank < candidates.length; rank += 1) {
      if (marked[rank] === 0) continue;
      const site = candidates[rank]!;
      if (site.domainAndRegistry === navigated.domainAndRegistry) continue;
      let kind: SimilarDomain['kind'] | null = null;
      for (const siteSkeleton of site.skeletons) {
        if (isEditDistanceAtMostOne(skeleton, siteSkeleton) && !isLikelyEditDistanceFalsePositive(navigated, site)) kind = 'edit-distance';
        else if (hasOneCharacterSwap(skeleton, siteSkeleton) && !isLikelyCharacterSwapFalsePositive(navigated, site)) kind = 'character-swap';
        else continue;
        break;
      }
      // le couple ecarte l'est en entier, apres coup : le verifier pour chaque site coute plus que toutes les comparaisons
      if (kind !== null && !ownedBySkeletonCheck(navigated, site)) return { kind, target: site.domainAndRegistry };
    }
  }
  const withoutDiacritics = maybeRemoveDiacritics(navigated.domainAndRegistry);
  if (withoutDiacritics === navigated.domainAndRegistry) return null;
  for (const site of sites.byHostname.values()) {
    if (site.domainAndRegistry === navigated.domainAndRegistry) continue;
    if (hasOneCharacterSwap(withoutDiacritics, maybeRemoveDiacritics(site.domainAndRegistry))) return { kind: 'character-swap', target: site.domainAndRegistry };
  }
  return null;
}

/**
 * Chromium : `GetSimilarDomainFromTopBucket`, inversion seulement. La distance d'edition contre le top bucket, Chromium
 * la calcule sans jamais rien montrer (« too noisy, just record metrics », `GetActionForMatchType`) : elle n'est pas
 * portee, et ne masque donc pas les controles suivants (chez Chromium, ce resultat muet arrete la recherche).
 */
function similarDomainFromTopBucket(navigated: DomainInfo): SimilarDomain | null {
  if (topBucketEditDistance === null) {
    const byLength = new Map<number, string[]>();
    for (const skeleton of topBucketEditDistanceSkeletons()) {
      const list = byLength.get(skeleton.length);
      if (list === undefined) byLength.set(skeleton.length, [skeleton]);
      else list.push(skeleton);
    }
    topBucketEditDistance = byLength;
  }
  for (const skeleton of navigated.skeletons) {
    for (const topSkeleton of topBucketEditDistance.get(skeleton.length) ?? []) {
      if (!hasOneCharacterSwap(skeleton, topSkeleton)) continue;
      const top = lookupSkeletonInTopDomains(topSkeleton, false);
      if (top !== null && !isLikelyCharacterSwapFalsePositive(navigated, domainInfo(top.domain))) return { kind: 'character-swap', target: top.domain };
    }
  }
  return null;
}

/**
 * Le site qu'un nom (decode) imite a une lettre ou a une inversion pres (Chromium, `GetMatchingDomain` apres les
 * squelettes) : un site du coffre a une lettre ajoutee, otee ou changee pres (`mabanqe.fr`) ou a deux lettres interverties
 * pres (`mabnaque.fr`), puis un des sites les plus visites a deux lettres interverties pres (`goolge.com`). Memes
 * conditions que l'enchassement (`lookalikeCandidate`). `null` s'il n'en imite aucun.
 *
 * Comme pour les squelettes, un site du coffre est une cible, jamais une excuse : seuls ceux du meme domaine
 * enregistrable que le nom juge sont ecartes.
 */
export function similarDomain(hostname: string, sites: EngagedSites = NO_SITES): SimilarDomain | null {
  const navigated = lookalikeCandidate(hostname);
  if (navigated === null || !isEditDistanceCandidate(navigated)) return null;
  return similarDomainFromVaultSites(navigated, sites) ?? similarDomainFromTopBucket(navigated);
}

/** Chromium : `kMinE2LDLengthForTargetEmbedding` -- un domaine de 4 lettres ou moins (`vk.com`, `ebay.com`) n'est pas protege. */
const MIN_E2LD_LENGTH_FOR_TARGET_EMBEDDING = 4;
/** Chromium : `kLocalAdditionalCommonWords`, en plus de la liste des mots courants. */
const LOCAL_ADDITIONAL_COMMON_WORDS: ReadonlySet<string> = new Set(['asahi', 'hoteles', 'jharkhand', 'nifty']);
/** Chromium : `kDomainsPermittedInEndEmbeddings` -- proteges malgre leur mot courant, sauf en toute fin (`foo-office.com`). */
const DOMAINS_PERMITTED_IN_END_EMBEDDINGS: readonly string[] = ['office.com', 'medium.com', 'orange.fr'];

let commonWords: ReadonlySet<string> | null = null;

/** Chromium : `SplitDomainIntoTokens` -- les morceaux separes par `.` ou `-`. */
function domainTokens(hostname: string): string[] {
  return hostname
    .split(/[-.]/u)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** Chromium : `GetMatchingTopDomainWithoutSeparators` -- `googlecom` -> google.com (top bucket seulement). */
function matchingTopDomainWithoutSeparators(token: string): string | null {
  for (const skeleton of hostSkeletons(token)) {
    const match = lookupSkeletonInTopDomains(skeleton, true);
    if (match !== null && match.separatorsRemoved) return match.domain;
  }
  return null;
}

/** Chromium : `IsETLDPlusOneOrTrivialSubdomain`. */
function isEtldPlusOneOrTrivialSubdomain(site: DomainInfo): boolean {
  return site.domainAndRegistry === site.hostname || `www.${site.domainAndRegistry}` === site.hostname;
}

/**
 * Chromium : `DoesETLDPlus1MatchTopDomainOrEngagedSite` -- le site du coffre d'abord (hors ceux du domaine `excluded`),
 * le top bucket ensuite.
 */
function etldPlusOneTarget(domain: DomainInfo, sites: EngagedSites, excluded: string): string | null {
  for (const skeleton of domain.skeletons) {
    const site = sites.bySkeleton().get(skeleton)?.find((candidate) => candidate.domainAndRegistry !== excluded);
    if (site !== undefined) return site.domainAndRegistry;
  }
  for (const skeleton of domain.skeletons) {
    const match = lookupSkeletonInTopDomains(skeleton, false);
    if (match !== null && isTopBucket(match.domain)) return match.domain;
  }
  return null;
}

/** Chromium : `UsesCommonWord` (`weather.com`, `jobs.com`) ; la liste distante du composant Safety Tips n'est pas embarquee. */
function usesCommonWord(domain: DomainInfo): boolean {
  if (DOMAINS_PERMITTED_IN_END_EMBEDDINGS.includes(domain.domainAndRegistry)) return false;
  commonWords ??= new Set(IDN_COMMON_WORDS.split(' '));
  return commonWords.has(domain.domainWithoutRegistry) || LOCAL_ADDITIONAL_COMMON_WORDS.has(domain.domainWithoutRegistry);
}

/** Chromium : `IsEmbeddingItself` -- `foo.example.com` enchasse dans `example.com` est le site lui-meme. */
function isEmbeddingItself(labels: readonly string[], embeddingDomain: string): boolean {
  let potential = labels[labels.length - 1]!;
  for (let i = labels.length - 1; i > 0; i -= 1) {
    potential = `${labels[i - 1]}.${potential}`;
    if (potential === embeddingDomain) return true;
  }
  return false;
}

/**
 * Chromium : `IsAllowedToBeEmbedded` -- mot courant, le site lui-meme, meme nom sous une autre zone (`google.com.mx`),
 * fin permise (`foo-office.com`). La liste distante des cibles admises (`ASubdomainIsAllowlisted`) n'est pas embarquee.
 */
function isAllowedToBeEmbedded(target: DomainInfo, labels: readonly string[], embeddingDomain: string): boolean {
  return (
    usesCommonWord(target) ||
    isEmbeddingItself(labels, embeddingDomain) ||
    e2ldWithDeFactoPublicRegistries(target.domainAndRegistry) === e2ldWithDeFactoPublicRegistries(embeddingDomain) ||
    DOMAINS_PERMITTED_IN_END_EMBEDDINGS.some((domain) => target.domainAndRegistry === domain && embeddingDomain.endsWith(`-${domain}`))
  );
}

export interface TargetEmbedding {
  /**
   * `interstitial` : le nom enchasse est suivi d'autre chose (`google.com-login.net`) -- Chromium bloque la page ;
   * `safety-tip` : il est tout au bout (`www-google.com`) -- Chromium previent.
   */
  readonly kind: 'interstitial' | 'safety-tip';
  /** Le site enchasse (`google.com`). */
  readonly target: string;
}

/**
 * Chromium : `SearchForEmbeddings`, balayage de la fin du nom vers son debut. Les sites du coffre du meme domaine
 * enregistrable que le nom juge sont ecartes (voir `targetEmbedding`).
 */
function searchForEmbeddings(hostname: string, sites: EngagedSites, safetyTipsAllowed: boolean): TargetEmbedding | null {
  const embeddingDomain = domainInfo(hostname).domainAndRegistry;
  const tokens = domainTokens(hostname);
  for (let end = tokens.length; end > 0; end -= 1) {
    const checkLabels = tokens.slice(0, end);
    const checkHost = checkLabels.join('.');
    const lastToken = tokens[end - 1]!;
    // un domaine tres visite colle sans separateurs (`googlecom`), cherche avant d'ecarter les zones invalides
    const glued = matchingTopDomainWithoutSeparators(lastToken);
    if (glued !== null) {
      const target = domainInfo(glued);
      const spoofed = domainTokens(checkHost.slice(0, checkHost.length - lastToken.length) + glued);
      if (byteLength(target.domainWithoutRegistry) > MIN_E2LD_LENGTH_FOR_TARGET_EMBEDDING && !isAllowedToBeEmbedded(target, spoofed, embeddingDomain)) {
        return { kind: 'interstitial', target: glued };
      }
    }
    const check = domainInfo(checkHost);
    if (check.domainWithoutRegistry === '' || byteLength(check.domainWithoutRegistry) <= MIN_E2LD_LENGTH_FOR_TARGET_EMBEDDING) continue;
    // un site du coffre a l'identique, parmi tous les noms qui finissent ici (le plus precis l'emporte)
    for (let start = 0; start < end - 1; start += 1) {
      const labels = tokens.slice(start, end);
      const embedded = domainInfo(labels.join('.'));
      const site = sites.byHostname.get(embedded.hostname);
      if (site === undefined || site.domainAndRegistry === embeddingDomain || isAllowedToBeEmbedded(embedded, labels, embeddingDomain)) continue;
      // tout au bout (`evil-google.com`) : simple avertissement, et seulement s'il est permis ; ailleurs, blocage
      if (end !== tokens.length) return { kind: 'interstitial', target: site.hostname };
      if (safetyTipsAllowed) return { kind: 'safety-tip', target: site.hostname };
    }
    // sinon, le squelette du domaine enregistrable d'un site du coffre ou du top bucket
    const target = etldPlusOneTarget(check, sites, embeddingDomain);
    if (target !== null && !isAllowedToBeEmbedded(check, checkLabels, embeddingDomain)) {
      if (end !== tokens.length) return { kind: 'interstitial', target };
      if (safetyTipsAllowed) return { kind: 'safety-tip', target };
    }
  }
  return null;
}

/** Chromium : `IsTopDomain` -- le domaine enregistrable (registres publics) est un domaine tres visite ou l'un de ses sous-domaines. */
function isTopDomain(ascii: string): boolean {
  const registrable = knownRegistrableDomain(ascii, false);
  return registrable !== null && isDomainAndRegistryATopDomain(unicodeHostname(registrable));
}

/** Chromium : `IsDomainAndRegistryATopDomain` -- ce domaine (decode) est un domaine tres visite ou l'un de ses sous-domaines. */
function isDomainAndRegistryATopDomain(domain: string): boolean {
  if (domain === '') return false;
  for (const skeleton of hostSkeletons(domain)) {
    const match = lookupSkeletonInTopDomains(skeleton, false);
    if (match !== null && (domain === match.domain || domain.endsWith(`.${match.domain}`))) return true;
  }
  return false;
}

/**
 * Les conditions de `CheckUrlForLookalikes` de Chromium : nom sous un registre connu, hors `.gov` / `.mil`, qui n'est pas
 * lui-meme un domaine tres visite. Le nom (decode) a juger, ou `null`.
 */
function lookalikeCandidate(hostname: string): DomainInfo | null {
  const ascii = asciiHostname(hostname);
  if (ascii === null || ascii.endsWith('.gov') || ascii.endsWith('.mil') || isTopDomain(ascii)) return null;
  const navigated = domainInfo(hostname);
  return navigated.domainAndRegistry === '' ? null : navigated;
}

/**
 * Le site qu'un nom (decode) enchasse, et la gravite que lui donne Chromium (`GetTargetEmbeddingType`, sous les
 * conditions de `lookalikeCandidate`). `null` s'il n'en enchasse aucun.
 *
 * Chez Chromium, un site frequente n'est jamais juge. Dans un coffre, chaque entree est un site frequente, et une
 * adresse d'hameconnage enregistree deux fois s'innocenterait elle-meme : les sites du coffre servent donc de CIBLES,
 * jamais d'excuse -- ceux du meme domaine enregistrable que le nom juge sont simplement ecartes.
 */
export function targetEmbedding(hostname: string, sites: EngagedSites = NO_SITES): TargetEmbedding | null {
  const navigated = lookalikeCandidate(hostname);
  if (navigated === null) return null;
  // le balayage trouve d'abord l'enchassement en bout de nom (simple avertissement) : on cherche alors encore un blocage
  const found = searchForEmbeddings(navigated.hostname, sites, true);
  if (found?.kind !== 'safety-tip') return found;
  return searchForEmbeddings(navigated.hostname, sites, false) ?? found;
}

/**
 * Chromium : `kBrandNamesForCSQ` (`lookalike_url_util.cc`, `main` du 2026-10-03) -- chaque marque et son squelette, tels
 * quels. Ecart voulu : Chromium y inverse les deux pour udemy (`{"uderny", "udemy"}`), si bien que `udemy-login.com`
 * n'est jamais vu (son squelette est `uderny-login`) et que le site propose serait uderny.com ; ici `udemy` / `uderny`.
 */
const COMBO_SQUATTING_BRANDS: readonly (readonly [string, string])[] = [
  ['adobe', 'adobe'],
  ['airbnb', 'airbnb'],
  ['alibaba', 'alibaba'],
  ['aliexpress', 'aliexpress'],
  ['amazon', 'arnazon'],
  ['baidu', 'baidu'],
  ['bestbuy', 'bestbuy'],
  ['blogspot', 'blogspot'],
  ['costco', 'costco'],
  ['craigslist', 'craigslist'],
  ['dropbox', 'dropbox'],
  ['expedia', 'expedia'],
  ['facebook', 'facebook'],
  ['fedex', 'fedex'],
  ['flickr', 'flickr'],
  ['github', 'github'],
  ['glassdoor', 'glassdoor'],
  ['gofundme', 'gofundrne'],
  ['google', 'google'],
  ['homedepot', 'hornedepot'],
  ['icloud', 'icloud'],
  ['indeed', 'indeed'],
  ['instagram', 'instagrarn'],
  ['intuit', 'intuit'],
  ['microsoft', 'rnicrosoft'],
  ['nbcnews', 'nbcnews'],
  ['netflix', 'netflix'],
  ['norton', 'norton'],
  ['nytimes', 'nytirnes'],
  ['office365', 'office365'],
  ['paypal', 'paypal'],
  ['pinterest', 'pinterest'],
  ['playstation', 'playstation'],
  ['quora', 'quora'],
  ['reddit', 'reddit'],
  ['reuters', 'reuters'],
  ['samsung', 'sarnsung'],
  ['spotify', 'spotify'],
  ['stackexchange', 'stackexchange'],
  ['stackoverflow', 'stackoverflow'],
  ['trello', 'trello'],
  ['twitch', 'twitch'],
  ['twitter', 'twitter'],
  ['udemy', 'uderny'],
  ['wikipedia', 'wikipedia'],
  ['wordpress', 'wordpress'],
  ['xfinity', 'xfinity'],
  ['yahoo', 'yahoo'],
  ['youtube', 'youtube'],
  ['zillow', 'zillow'],
];

/** Chromium : `kSkeletonsOfPopularKeywordsForCSQ` -- des squelettes (`adrnin` pour admin), tels quels. */
const COMBO_SQUATTING_KEYWORDS: readonly string[] = [
  'account',
  'activate',
  'adrnin',
  'coin',
  'crypto',
  'login',
  'logout',
  'password',
  'secure',
  'security',
  'signin',
  'signout',
  'wallet',
];

/** Chromium : `ComboSquattingParams` -- les marques (nom, squelette) et les squelettes des mots-cles. */
export interface ComboSquattingParams {
  readonly brands: readonly (readonly [string, string])[];
  readonly keywords: readonly string[];
}

export const COMBO_SQUATTING_PARAMS: ComboSquattingParams = { brands: COMBO_SQUATTING_BRANDS, keywords: COMBO_SQUATTING_KEYWORDS };

/** Chromium : `kMinBrandNameLengthForComboSquatting` -- une marque de 4 lettres ou moins (`len.com`) n'est pas cherchee. */
const MIN_BRAND_NAME_LENGTH_FOR_COMBO_SQUATTING = 4;

/** Chromium : `IsComboSquattingCandidate`. */
function isComboSquattingCandidate(brand: string): boolean {
  return brand.length > MIN_BRAND_NAME_LENGTH_FOR_COMBO_SQUATTING;
}

/**
 * Chromium : `IsComboSquatting` -- la premiere marque dont un squelette du nom hors registre contient le squelette, a
 * cote (sans chevauchement) d'un mot-cle qui n'est ni contenu dans la marque ni ne la contient (`keyservices` : `vices`
 * est dans `services`) ; le nom n'est pas la marque elle-meme. La marque trouvee, ou `null`.
 */
function comboSquattingBrand(navigated: DomainInfo, brands: readonly (readonly [string, string])[], keywords: readonly string[]): string | null {
  for (const [brandName, brandSkeleton] of brands) {
    for (const skeleton of navigated.domainWithoutRegistrySkeletons) {
      const brandPos = skeleton.indexOf(brandSkeleton);
      if (skeleton.length === brandSkeleton.length || brandPos === -1) continue;
      for (const keyword of keywords) {
        const keywordPos = skeleton.indexOf(keyword);
        if (keywordPos === -1) continue;
        if (brandSkeleton.includes(keyword) || keyword.includes(brandSkeleton)) continue;
        if ((keywordPos > brandPos && keywordPos < brandPos + brandSkeleton.length) || (brandPos > keywordPos && brandPos < keywordPos + keyword.length)) continue;
        return brandName;
      }
    }
  }
  return null;
}

/**
 * Chromium : `FindMatchedDomainForHardCodedComboSquatting` -- la marque sous le registre du nom quand c'est un domaine tres
 * visite (`google-login.co.kr` -> google.co.kr), sinon la marque en `.com`.
 */
function matchedDomainForBrand(brandName: string, navigated: DomainInfo): string {
  const registry = navigated.asciiDomainAndRegistry.slice(navigated.asciiDomainWithoutRegistry.length + 1);
  const suggested = domainInfo(`${brandName}.${registry}`);
  return isDomainAndRegistryATopDomain(suggested.domainAndRegistry) ? suggested.hostname : `${brandName}.com`;
}

export interface ComboSquatting {
  /** `brand` : une marque de la liste de Chromium ; `vault-site` : le nom d'un site du coffre. */
  readonly kind: 'brand' | 'vault-site';
  /** Le site imite : la marque sous son domaine, ou le site du coffre (nom d'hote). */
  readonly target: string;
}

/**
 * Le site dont un nom (decode) accole le nom a un mot d'appel -- `google-login.com`, `youtubesecure.com`,
 * `mabanque-secure.fr` pour un coffre qui connait mabanque.fr (Chromium : `GetComboSquattingType`, sous les conditions de
 * `lookalikeCandidate`) : les marques de Chromium d'abord, les sites du coffre ensuite, le premier dans l'ordre du coffre
 * l'emportant (`FindMatchedDomainForSiteEngagementComboSquatting`). `null` s'il n'en imite aucun.
 *
 * Ecart voulu : Chromium ne montre cet avertissement que si sa configuration distante l'active
 * (`IsHeuristicEnabledForHostname`) ; ici il est toujours montre. Un site du coffre reste une cible, jamais une excuse : le
 * nom qui est la marque elle-meme (meme nom hors registre) n'est simplement pas compare. `params` : les listes de Chromium
 * (`SetComboSquattingParamsForTesting` pour ses vecteurs).
 */
export function comboSquatting(hostname: string, sites: EngagedSites = NO_SITES, params: ComboSquattingParams = COMBO_SQUATTING_PARAMS): ComboSquatting | null {
  const navigated = lookalikeCandidate(hostname);
  if (navigated === null) return null;
  // sans aucun mot-cle, aucune marque ne peut aboutir : rien a parcourir (un coffre de milliers de sites)
  if (![...navigated.domainWithoutRegistrySkeletons].some((skeleton) => params.keywords.some((keyword) => skeleton.includes(keyword)))) return null;
  const brand = comboSquattingBrand(navigated, params.brands, params.keywords);
  if (brand !== null) return { kind: 'brand', target: matchedDomainForBrand(brand, navigated) };
  const vaultBrand = comboSquattingBrand(navigated, sites.comboSquattingBrands(), params.keywords);
  if (vaultBrand === null) return null;
  for (const site of sites.byHostname.values()) if (site.asciiDomainWithoutRegistry === vaultBrand) return { kind: 'vault-site', target: site.hostname };
  return null;
}

function labelScripts(label: string): Set<string> {
  const scripts = new Set<string>();
  for (const char of label) {
    for (const [name, pattern] of CONFUSABLE_SCRIPTS) if (pattern.test(char)) scripts.add(name);
  }
  return scripts;
}

export interface DeceptiveHost {
  /** Le nom tel que le reseau le voit (punycode) : la seule forme qui ne ment pas. */
  readonly ascii: string;
  /** Le nom tel qu'il s'affiche une fois decode. */
  readonly unicode: string;
  readonly reason:
    | 'mixed-script'
    | 'whole-script'
    | 'digit-lookalike'
    | 'zone-character'
    | 'middle-dot'
    | 'dangerous-pattern'
    | 'disallowed-character'
    | 'vault-site'
    | 'top-domain'
    | 'edit-distance'
    | 'character-swap'
    | 'target-embedding'
    | 'combo-squatting';
  /** Le nom latin que l'adresse imite (`apple.com`), quand chaque lettre suspecte a un sosie connu ; sinon `null`. */
  readonly imitates: string | null;
}

/**
 * Un nom d'hote (deja normalise : minuscules, punycode) est-il trompeur ? `null` s'il ne l'est pas. `sites` : les sites
 * du coffre, que l'enchassement vise en plus des domaines tres visites (`engagedSites`).
 */
export function deceptiveHostname(hostname: string, sites: EngagedSites = NO_SITES): DeceptiveHost | null {
  const idn = idnDeceptiveHostname(hostname, sites);
  if (idn !== null) return idn;
  // l'enchassement, comme chez Chromium apres le squelette : sur le nom decode, celui que lit l'utilisateur
  const unicode = unicodeHostname(hostname);
  if (unicode.split('.').some((label) => label.startsWith('xn--'))) return null;
  // une lettre ou une inversion pres, cherchee par Chromium avant l'enchassement
  const similar = similarDomain(unicode, sites);
  if (similar !== null) return { ascii: hostname, unicode, reason: similar.kind, imitates: similar.target };
  const embedding = targetEmbedding(unicode, sites);
  if (embedding !== null) return { ascii: hostname, unicode, reason: 'target-embedding', imitates: embedding.target };
  // une marque accolee a un mot d'appel, en dernier comme chez Chromium
  const combo = comboSquatting(unicode, sites);
  return combo === null ? null : { ascii: hostname, unicode, reason: 'combo-squatting', imitates: combo.target };
}

/**
 * La phrase qui dit pourquoi une adresse trompe : la forme reelle (punycode) d'un nom internationalise, le domaine qui
 * possede vraiment un nom qui en enchasse un autre (`google.com-login.net` est un site de com-login.net).
 */
export function deceptiveHostReason(found: DeceptiveHost, separator = ' : '): string {
  const imitated = found.imitates ?? 'le site qu’elle imite';
  if (found.reason === 'target-embedding') {
    const owner = domainInfo(found.unicode).domainAndRegistry || found.unicode;
    return `${found.unicode} n’est pas ${imitated}${separator}c’est une adresse de ${owner}`;
  }
  if (found.reason === 'edit-distance') return `${found.unicode} n’est pas ${imitated}${separator}elles ne different que d’une lettre a l’oeil`;
  if (found.reason === 'character-swap') return `${found.unicode} n’est pas ${imitated}${separator}deux lettres y sont interverties`;
  if (found.reason === 'combo-squatting') return `${found.unicode} n’est pas ${imitated}${separator}un mot comme « login » ou « secure » y est accole a son nom`;
  return `${found.unicode} n’est pas ${imitated}${separator}son adresse reelle est ${found.ascii}`;
}

/** Les controles du nom internationalise (UTS #39, Chromium `IDNSpoofChecker`). */
function idnDeceptiveHostname(hostname: string, sites: EngagedSites): DeceptiveHost | null {
  const labels = hostname.split('.');
  if (labels.length < 2 || !labels.some((label) => label.startsWith('xn--'))) return null;
  const decoded = labels.map((label) => (label.startsWith('xn--') ? decodePunycode(label.slice(4)) : label));
  if (decoded.some((label) => label === null)) return null;
  const unicodeLabels = decoded as string[];
  const tld = unicodeLabels[unicodeLabels.length - 1]!;
  const tldScripts = labelScripts(tld);
  let reason: DeceptiveHost['reason'] | null = null;
  const digitLabels = new Set<number>();
  // la zone est jugee comme les autres labels, avec elle-meme pour zone (Chromium : `example.com丶test`)
  for (const [index, label] of unicodeLabels.entries()) {
    const mix = scriptMix(label);
    // l'ordre de Chromium : chiffres d'abord quand les ecritures se melangent (`৪੨`), apres le sosie entier sinon
    if (mix === 'mixed' || (mix === 'latin-cjk' && NON_ASCII_LATIN_LETTER.test(label))) {
      if (isDigitLookalike(label)) digitLabels.add(index);
      reason = digitLabels.has(index) ? 'digit-lookalike' : 'mixed-script';
      break;
    }
    // l'ordre de Chromium : caracteres de zone et point median juste apres le melange (verifie par ICU)
    if (hasZoneCharacter(label, tld)) {
      reason = 'zone-character';
      break;
    }
    if (hasUnsafeMiddleDot(label, tld)) {
      reason = 'middle-dot';
      break;
    }
    // latin + CJK, un kana d'exception ou un accent combinant : Chromium saute le sosie entier et passe aux motifs
    if (mix === 'latin-cjk' || KANA_LETTER_EXCEPTIONS.test(label) || COMBINING_DIACRITICS.test(label)) {
      if (isDigitLookalike(label)) {
        digitLabels.add(index);
        reason = 'digit-lookalike';
      } else if (DANGEROUS_PATTERN.test(label)) {
        reason = 'dangerous-pattern';
        break;
      }
      continue;
    }
    const scripts = labelScripts(label);
    const [script] = scripts;
    const confusable = script === undefined ? undefined : WHOLE_SCRIPT_CONFUSABLES.get(script);
    if (confusable !== undefined) {
      const inZone = confusable.tlds.has(tld) || tldScripts.has(script!);
      // les caracteres de l'ecriture du label, comme Chromium : un chiffre thai (`๐`) ou le geresh hebreu (`׳`) comptent
      const letters = [...label].filter((char) => SCRIPT_PATTERNS.get(script!)!.test(char));
      if (!inZone && letters.length > 0 && letters.every((char) => confusable.latinAlike.has(char)) && !ALLOWED_WHOLE_SCRIPT_WORDS.has(label)) {
        reason = 'whole-script';
        continue;
      }
    }
    if (isDigitLookalike(label)) {
      digitLabels.add(index);
      reason = 'digit-lookalike';
    }
  }
  // le jeu de caracteres admis (ICU, avant tout le reste chez Chromium) : en dernier ici, pour garder le motif le plus
  // parlant quand un autre controle a deja vu le label ; seuls les labels internationalises (`xn--`) y passent, comme chez
  // Chromium qui ne juge pas un label ASCII (`_dmarc`)
  if (reason === null && unicodeLabels.some((label, index) => labels[index]!.startsWith('xn--') && hasDisallowedCharacter(label))) {
    reason = 'disallowed-character';
  }
  const unicode = unicodeLabels.join('.');
  // le squelette d'un site du coffre (`mabanqué.fr` -> mabanque.fr), cherche par Chromium avant les domaines tres visites
  const vaultSite = similarVaultSite(unicode, sites);
  if (reason === null && vaultSite !== null) return { ascii: hostname, unicode, reason: 'vault-site', imitates: vaultSite };
  // le squelette d'un domaine tres visite (`googlé.com` -> google.com) : Chromium le juge apres tous les labels, sur le nom entier
  const topDomain = reason === null ? similarTopDomain(unicode) : null;
  if (topDomain !== null) return { ascii: hostname, unicode, reason: 'top-domain', imitates: topDomain };
  if (reason === null) return null;
  // un label de sosies de chiffres imite des chiffres (`1շ` -> `12`), pas des lettres
  const skeleton = unicodeLabels
    .map((label, index) => [...label].map((char) => (digitLabels.has(index) ? DIGIT_ALIKE.get(char) : undefined) ?? LATIN_ALIKE.get(char) ?? char).join(''))
    .join('.');
  return { ascii: hostname, unicode, reason, imitates: /^[\x21-\x7e]+$/u.test(skeleton) ? skeleton : similarTopDomain(unicode) };
}

/** Premiere adresse trompeuse parmi des URI d'entree ; `null` si aucune. Une adresse hors web n'est jamais trompeuse ici. */
export function findDeceptiveUri(uris: readonly string[], sites: EngagedSites = NO_SITES): DeceptiveHost | null {
  for (const uri of uris) {
    const host = siteHostname(uri);
    if (host === null) continue;
    const found = deceptiveHostname(host, sites);
    if (found !== null) return found;
  }
  return null;
}
